/**
 * Sentry Sanitization Utilities
 * Prevents PII/PHI and sensitive data leakage to Sentry
 */

/**
 * List of sensitive header names that should be redacted
 */
const SENSITIVE_HEADERS = [
  'x-auth',
  'x-csrf-token',
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'api-key',
  'accountid',
  'facilityid',
];

/**
 * List of sensitive field names in request/response data that should be redacted
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'auth',
  'csrf',
  'secret',
  'key',
  'apiKey',
  'api_key',
  'accessToken',
  'refreshToken',
  'sessionId',
  'session_id',
  'email',
  'phone',
  'ssn',
  'socialSecurityNumber',
  'creditCard',
  'cardNumber',
  'cvv',
  'accountId',
  'facilityId',
  'userId',
  'user_id',
  'patientId',
  'patient_id',
  'name',
  'firstName',
  'lastName',
  'address',
  'zipCode',
  'zip',
  'dateOfBirth',
  'dob',
  'medicalRecordNumber',
  'mrn',
  // Healthcare-specific PHI fields
  'admissionDate',
  'admission_date',
  'dischargeDate',
  'discharge_date',
  'admissionNumber',
  'admission_number',
  'encounterNumber',
  'encounter_number',
  'encounterId',
  'encounter_id',
  'visitNumber',
  'visit_number',
  'diagnosisCode',
  'diagnosis_code',
  'icdCode',
  'icd_code',
  'cptCode',
  'cpt_code',
  'procedureCode',
  'procedure_code',
  'npi',
  'nationalProviderId',
  'deaNumber',
  'dea_number',
  'ccn',
  'certificationNumber',
  'wardNumber',
  'ward_number',
  'roomNumber',
  'room_number',
  'bedNumber',
  'bed_number',
  'physician',
  'doctor',
  'provider',
  'diagnosis',
  'medication',
  'treatment',
  'insurance',
  'insurer',
  'payer',
  'memberId',
  'member_id',
  'subscriberId',
  'subscriber_id',
  'groupNumber',
  'group_number'
];

/**
 * Patterns to detect PII/PHI in strings
 */
const PII_PATTERNS = {
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  phone: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  ipAddress: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
  // Healthcare-specific patterns
  mrn: /\bMRN[-\s]?[0-9]{6,10}\b/gi,
  npi: /\b[0-9]{10}\b/g,
  icdCode: /\b[A-Z][0-9]{2}\.?[0-9A-Z]{0,4}\b/g,
  datePattern: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/g
};

/**
 * Redacts sensitive values in an object recursively
 * @param {any} obj - Object to sanitize
 * @param {string[]} sensitiveKeys - Array of keys to redact
 * @returns {any} Sanitized object
 */
export function redactSensitiveFields(obj, sensitiveKeys = SENSITIVE_FIELDS) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== 'object') {
    return redactString(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => redactSensitiveFields(item, sensitiveKeys));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    // Check if this key should be redacted using exact match or common separators
    // This prevents false positives (e.g., "monkey" matching "key") and false negatives
    const shouldRedact = sensitiveKeys.some(sensitiveKey => {
      const lowerSensitiveKey = sensitiveKey.toLowerCase();
      return (
        lowerKey === lowerSensitiveKey ||
        lowerKey.endsWith(`_${lowerSensitiveKey}`) ||
        lowerKey.startsWith(`${lowerSensitiveKey}_`) ||
        lowerKey.endsWith(`-${lowerSensitiveKey}`) ||
        lowerKey.startsWith(`${lowerSensitiveKey}-`) ||
        lowerKey.endsWith(`.${lowerSensitiveKey}`) ||
        lowerKey.startsWith(`${lowerSensitiveKey}.`)
      );
    });

    if (shouldRedact) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = redactSensitiveFields(value, sensitiveKeys);
    } else if (typeof value === 'string') {
      sanitized[key] = redactString(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Redacts PII patterns in strings
 * @param {any} value - Value to check and redact
 * @returns {any} Redacted value
 */
export function redactString(value) {
  if (typeof value !== 'string') {
    return value;
  }

  let redacted = value;

  // General PII
  redacted = redacted.replace(PII_PATTERNS.email, '[EMAIL_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.phone, '[PHONE_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.ssn, '[SSN_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.creditCard, '[CARD_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.ipAddress, '[IP_REDACTED]');

  // Healthcare-specific
  redacted = redacted.replace(PII_PATTERNS.mrn, '[MRN_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.npi, '[NPI_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.icdCode, '[ICD_REDACTED]');
  redacted = redacted.replace(PII_PATTERNS.datePattern, '[DATE_REDACTED]');

  return redacted;
}

/**
 * Sanitizes HTTP request/response data
 * @param {Object} request - Request object from Sentry event
 * @returns {Object} Sanitized request object
 */
export function sanitizeRequest(request) {
  if (!request) {
    return request;
  }

  const sanitized = { ...request };

  // Sanitize headers
  if (sanitized.headers) {
    const sanitizedHeaders = {};
    for (const [key, value] of Object.entries(sanitized.headers)) {
      const lowerKey = key.toLowerCase();
      const shouldRedact = SENSITIVE_HEADERS.some(header => {
        const lowerHeader = header.toLowerCase();
        return (
          lowerKey === lowerHeader ||
          lowerKey.endsWith(`-${lowerHeader}`) ||
          lowerKey.startsWith(`${lowerHeader}-`)
        );
      });
      sanitizedHeaders[key] = shouldRedact ? '[REDACTED]' : value;
    }
    sanitized.headers = sanitizedHeaders;
  }

  // Sanitize cookies
  if (sanitized.cookies) {
    sanitized.cookies = '[REDACTED]';
  }

  // Sanitize data/body
  if (sanitized.data) {
    sanitized.data = redactSensitiveFields(sanitized.data);
  }

  // Sanitize query string
  if (sanitized.query_string) {
    if (typeof sanitized.query_string === 'string') {
      sanitized.query_string = redactString(sanitized.query_string);
    } else {
      sanitized.query_string = redactSensitiveFields(sanitized.query_string);
    }
  }

  return sanitized;
}

/**
 * Sanitizes breadcrumb data
 * @param {Object} breadcrumb - Breadcrumb object from Sentry
 * @returns {Object|null} Sanitized breadcrumb or null if should be dropped
 */
export function sanitizeBreadcrumb(breadcrumb) {
  if (!breadcrumb) {
    return breadcrumb;
  }

  // Drop console logs to prevent PHI/PII leakage
  if (breadcrumb.category === 'console') {
    if (breadcrumb.level === 'log' || breadcrumb.level === 'debug' || breadcrumb.level === 'info') {
      return null;
    }
  }

  const sanitized = { ...breadcrumb };

  // Sanitize data in breadcrumb
  if (sanitized.data) {
    sanitized.data = redactSensitiveFields(sanitized.data);

    // If breadcrumb is an HTTP request, sanitize it properly
    if (sanitized.category === 'fetch' || sanitized.category === 'xhr') {
      sanitized.data = sanitizeRequest(sanitized.data);

      // Drop response bodies
      if (sanitized.data.response) {
        sanitized.data.response = {
          status: sanitized.data.response?.status,
          statusText: '[REDACTED]'
        };
      }
    }
  }

  // Sanitize message
  if (sanitized.message) {
    sanitized.message = redactString(sanitized.message);
  }

  return sanitized;
}

/**
 * Sanitizes a Sentry event before sending
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Event hint
 * @returns {Object|null} Sanitized event or null to drop it
 */
export function sanitizeEvent(event, hint) {
  if (!event) {
    return event;
  }

  const sanitized = { ...event };

  // Sanitize top-level message (Sentry sometimes uses event.message)
  if (sanitized.message) {
    sanitized.message = redactString(sanitized.message);
  }

  // Sanitize request data
  if (sanitized.request) {
    sanitized.request = sanitizeRequest(sanitized.request);
  }

  // Sanitize contexts (may contain request/response data)
  if (sanitized.contexts) {
    const sanitizedContexts = { ...sanitized.contexts };
    
    if (sanitizedContexts.request) {
      sanitizedContexts.request = sanitizeRequest(sanitizedContexts.request);
    }
    
    sanitized.contexts = sanitizedContexts;
  }

  // Sanitize extra data
  if (sanitized.extra) {
    sanitized.extra = redactSensitiveFields(sanitized.extra);
  }

  // Sanitize tags
  if (sanitized.tags) {
    sanitized.tags = redactSensitiveFields(sanitized.tags);
  }

  // Sanitize user data
  if (sanitized.user) {
    sanitized.user = {
      id: sanitized.user.id ? '[REDACTED]' : undefined,
      username: sanitized.user.username ? '[REDACTED]' : undefined,
      email: sanitized.user.email ? '[REDACTED]' : undefined,
      ip_address: sanitized.user.ip_address ? '[REDACTED]' : undefined,
    };
  }

  // Sanitize breadcrumbs
  if (sanitized.breadcrumbs && Array.isArray(sanitized.breadcrumbs)) {
    sanitized.breadcrumbs = sanitized.breadcrumbs
      .map(breadcrumb => sanitizeBreadcrumb(breadcrumb))
      .filter(breadcrumb => breadcrumb !== null);
  }

  // Sanitize exception data
  if (sanitized.exception && Array.isArray(sanitized.exception.values)) {
    sanitized.exception.values = sanitized.exception.values.map(exception => {
      if (!exception) return exception;
      
      const sanitizedException = { ...exception };
      
      // Sanitize exception value/message
      if (sanitizedException.value) {
        sanitizedException.value = redactString(sanitizedException.value);
      }
      
      // Sanitize stacktrace
      if (sanitizedException.stacktrace && sanitizedException.stacktrace.frames) {
        sanitizedException.stacktrace.frames = sanitizedException.stacktrace.frames.map(frame => {
          if (!frame) return frame;
          
          const sanitizedFrame = { ...frame };
          
          // Sanitize local variables
          if (sanitizedFrame.vars) {
            sanitizedFrame.vars = redactSensitiveFields(sanitizedFrame.vars);
          }
          
          return sanitizedFrame;
        });
      }
      
      return sanitizedException;
    });
  }

  return sanitized;
}

/**
 * Sanitizes error object before sending to Sentry
 * Useful for axios interceptors and manual error logging
 * @param {Error} error - Error object
 * @returns {Error} Sanitized error object
 */
export function sanitizeError(error) {
  if (!error || !(error instanceof Error)) {
    return error;
  }

  // Create a new error with sanitized message
  const sanitizedError = new Error(redactString(error.message));
  sanitizedError.name = error.name;
  sanitizedError.stack = error.stack;

  // Sanitize any custom properties
  if (error.config) {
    sanitizedError.config = {
      ...error.config,
      headers: error.config.headers ? redactSensitiveFields(error.config.headers) : undefined,
      data: error.config.data ? redactSensitiveFields(error.config.data) : undefined,
      params: error.config.params ? redactSensitiveFields(error.config.params) : undefined,
    };
  }

  if (error.response) {
    sanitizedError.response = {
      ...error.response,
      data: error.response.data ? redactSensitiveFields(error.response.data) : undefined,
      headers: error.response.headers ? redactSensitiveFields(error.response.headers) : undefined,
    };
  }

  return sanitizedError;
}
